import { looksLikeScaleError, MISMATCH_THRESHOLD } from '@willie/index-comparison'
import type { TRPCOutputs } from '~/utils/trpc'

export type CaptureDetail = TRPCOutputs['ops']['indexCaptures']['get']

type Consumptions = Pick<CaptureDetail, 'indexConsumption' | 'telemetryConsumption'>

export type AlignmentVerdict = 'aligned' | 'not_aligned' | 'pending'

export const TOLERANCE_PERCENT = MISMATCH_THRESHOLD * 100

/** Relative to the index consumption, which is what the 5% threshold is measured against. */
export const gapPercent = ({
  indexConsumption,
  delta
}: {
  indexConsumption: number | null
  delta: number | null
}): number | null =>
  indexConsumption !== null && delta !== null && indexConsumption > 0
    ? (Math.abs(delta) / indexConsumption) * 100
    : null

export const isScaleError = ({ indexConsumption, telemetryConsumption }: Consumptions): boolean =>
  indexConsumption !== null &&
  telemetryConsumption !== null &&
  looksLikeScaleError({ indexConsumption, telemetryConsumption })

/** `overridden` is true when ops decided the verdict rather than the comparison. */
export const verdictOf = (detail: CaptureDetail): { verdict: AlignmentVerdict; overridden: boolean } => {
  if (detail.status === 'matched') return { verdict: 'aligned', overridden: false }
  if (detail.status === 'flagged') return { verdict: 'not_aligned', overridden: false }
  if (detail.status === 'resolved') {
    return { verdict: detail.resolution?.decision === 'aligned' ? 'aligned' : 'not_aligned', overridden: true }
  }
  return { verdict: 'pending', overridden: false }
}

export const expectedIndex = ({ anchor, telemetryConsumption }: CaptureDetail): number | null =>
  anchor && telemetryConsumption !== null ? anchor.value + telemetryConsumption : null

/** A logger that buffers can reach the data-lake up to a day after the photo it should cover. */
const TELEMETRY_SETTLE_MS = 24 * 60 * 60_000

export const comparedBeforeTelemetrySettled = ({
  capturedAt,
  comparedAt
}: Pick<CaptureDetail, 'capturedAt' | 'comparedAt'>): boolean =>
  capturedAt !== null &&
  comparedAt !== null &&
  new Date(comparedAt).getTime() - new Date(capturedAt).getTime() < TELEMETRY_SETTLE_MS

export const formatM3 = (value: number | null): string =>
  value === null ? '—' : `${value.toLocaleString('en-GB', { maximumFractionDigits: 3 })} m³`

export const formatPercent = (value: number | null): string =>
  value === null ? '—' : `${value.toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`
